/* Peças visuais da Emissão de NFS-e, no desenho do protótipo (vidro claro sobre a
   aurora da marca). Cores e medidas vêm dos tokens do design system "Especialistas". */
import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  ButtonProps,
  Drawer,
  DrawerBody,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerOverlay,
  Flex,
  Heading,
  IconButton,
  Spinner,
  Text,
  Tooltip,
} from '@chakra-ui/react';
import { animate, motion } from 'framer-motion';
import {
  CaretRight,
  CheckCircle,
  Circle,
  IconProps,
  Info,
  Prohibit,
  Warning,
  X,
  XCircle,
} from 'phosphor-react';
import { useNavigate } from 'react-router-dom';
import { GlassCard } from '../../../../../components/glass';
import {
  NfseAmbiente,
  NfseCertificadoSituacao,
  NfseEmissaoStatus,
  NfseEmitenteSituacao,
} from '../../../../../types/nfseEmissao';

export const C = {
  brand50: '#EBF5FE',
  brand100: '#DFF3FF',
  brand600: '#008CCC',
  brand700: '#0077B6',
  brand800: '#006D9E',
  brand900: '#075985',
  accent: '#1B88F5',
  ink: '#0F172A',
  ink2: '#334155',
  muted: '#475569',
  subtle: '#5B6676',
  placeholder: '#94A3B8',
  line: '#E4E4E7',
  lineInput: '#CBD5E1',
  lineSoft: '#F1F5F9',
  sunken: '#F8FAFC',
  success: '#15803D',
  successIcon: '#16A34A',
  successText: '#166534',
  successBg: '#DCFCE7',
  successSoft: '#F0FDF4',
  warning: '#B45309',
  warningText: '#92400E',
  warningBg: '#FEF3C7',
  warningSoft: '#FEFCE8',
  warningLine: '#FDE68A',
  danger: '#B91C1C',
  dangerText: '#991B1B',
  dangerBg: '#FEE2E2',
  dangerLine: '#FCA5A5',
  island: '#0A0C10',
  onIsland: '#F5F7FA',
  onIslandMuted: '#A3ACB9',
  islandAccent: '#38BDF8',
};

export const MONO = "'JetBrains Mono', ui-monospace, 'SF Mono', Menlo, Consolas, monospace";
export const PILL_GRADIENT = `linear-gradient(180deg, ${C.brand700} 0%, ${C.brand800} 100%)`;
export const PILL_SHADOW =
  'inset 0 1px 0 rgba(255, 255, 255, 0.28), 0 6px 16px -4px rgba(0, 109, 158, 0.45)';
export const SPRING = { type: 'spring', stiffness: 420, damping: 32 } as const;

export type Tone =
  | 'blue'
  | 'green'
  | 'indigo'
  | 'violet'
  | 'success'
  | 'warning'
  | 'danger'
  | 'neutral';

const TILE: Record<Tone, { bg: string; fg: string }> = {
  blue: { bg: C.brand100, fg: C.brand600 },
  green: { bg: '#E0F5E5', fg: '#22875A' },
  indigo: { bg: '#E0E7FF', fg: '#6366F1' },
  violet: { bg: '#EDE9FE', fg: '#7C5CE0' },
  success: { bg: C.successBg, fg: C.success },
  warning: { bg: C.warningBg, fg: C.warning },
  danger: { bg: C.dangerBg, fg: C.danger },
  neutral: { bg: C.lineSoft, fg: C.ink2 },
};

type PhosphorIcon = React.ComponentType<IconProps>;

export function IconTile({
  icon: Icone,
  tone = 'blue',
  size = 38,
  iconSize,
}: {
  icon: PhosphorIcon;
  tone?: Tone;
  size?: number;
  iconSize?: number;
}) {
  const t = TILE[tone];
  return (
    <Flex
      align="center"
      justify="center"
      w={`${size}px`}
      h={`${size}px`}
      flex="none"
      borderRadius={size >= 48 ? '18px' : '14px'}
      bg={t.bg}
      color={t.fg}
    >
      <Icone size={iconSize ?? Math.round(size * 0.48)} weight="duotone" />
    </Flex>
  );
}

export function Eyebrow({ children, ...rest }: React.ComponentProps<typeof Text>) {
  return (
    <Text
      fontFamily={MONO}
      fontSize="10.5px"
      lineHeight="14px"
      fontWeight={500}
      letterSpacing="1.4px"
      textTransform="uppercase"
      color={C.subtle}
      {...rest}
    >
      {children}
    </Text>
  );
}

// --- botões -------------------------------------------------------------------------

type Variante = 'primary' | 'secondary' | 'ghost' | 'danger';

const VARIANTE: Record<Variante, ButtonProps> = {
  primary: {
    bgImage: PILL_GRADIENT,
    color: 'white',
    boxShadow: PILL_SHADOW,
    _hover: { filter: 'brightness(1.06)' },
    _active: { transform: 'scale(0.96)' },
  },
  secondary: {
    bg: 'rgba(255, 255, 255, 0.78)',
    color: C.brand800,
    boxShadow: 'inset 0 0 0 1px rgba(15, 23, 42, 0.10), 0 1px 2px rgba(15, 23, 42, 0.05)',
    _hover: { bg: 'white' },
    _active: { transform: 'scale(0.96)' },
  },
  ghost: {
    bg: 'transparent',
    color: C.brand700,
    _hover: { bg: 'rgba(0, 119, 182, 0.08)' },
    _active: { transform: 'scale(0.96)' },
  },
  danger: {
    bg: C.danger,
    color: 'white',
    _hover: { bg: C.dangerText },
    _active: { transform: 'scale(0.96)' },
  },
};

export function NButton({
  variant = 'primary',
  icon: Icone,
  size = 'md',
  children,
  ...rest
}: Omit<ButtonProps, 'variant' | 'size'> & {
  variant?: Variante;
  icon?: PhosphorIcon;
  size?: 'sm' | 'md';
}) {
  return (
    <Button
      borderRadius="999px"
      h={size === 'sm' ? '32px' : '40px'}
      px={size === 'sm' ? 3.5 : 5}
      fontSize={size === 'sm' ? '12.5px' : '14px'}
      fontWeight={600}
      transition="transform 0.25s cubic-bezier(0.34, 1.56, 0.64, 1), filter 0.2s, background 0.2s"
      leftIcon={Icone ? <Icone size={size === 'sm' ? 15 : 17} weight="bold" /> : undefined}
      _focusVisible={{ boxShadow: `0 0 0 2px #FFFFFF, 0 0 0 4px ${C.accent}` }}
      _disabled={{ opacity: 0.45, cursor: 'not-allowed', transform: 'none', filter: 'none' }}
      {...VARIANTE[variant]}
      {...rest}
    >
      {children}
    </Button>
  );
}

export function NIconButton({
  icon: Icone,
  label,
  onClick,
  isDisabled,
}: {
  icon: PhosphorIcon;
  label: string;
  onClick?: (e: React.MouseEvent) => void;
  isDisabled?: boolean;
}) {
  return (
    <Tooltip label={label} openDelay={300}>
      <IconButton
        aria-label={label}
        icon={<Icone size={16} />}
        size="sm"
        borderRadius="999px"
        variant="ghost"
        color={C.muted}
        _hover={{ bg: C.brand50, color: C.brand700 }}
        onClick={onClick}
        isDisabled={isDisabled}
      />
    </Tooltip>
  );
}

// --- cabeçalho ----------------------------------------------------------------------

export interface Crumb {
  label: string;
  to?: string;
}

export function ModuleHeader({
  eyebrow = 'Emissão de NFS-e',
  title,
  description,
  icon,
  tone = 'blue',
  crumbs,
  actions,
}: {
  eyebrow?: string | null;
  title: string;
  description?: React.ReactNode;
  icon: PhosphorIcon;
  tone?: Tone;
  crumbs?: Crumb[];
  actions?: React.ReactNode;
}) {
  const navigate = useNavigate();
  return (
    <Box>
      {crumbs && (
        <Flex as="nav" aria-label="Caminho" align="center" gap={1} mb={3} wrap="wrap">
          {crumbs.map((c, i) => (
            <Flex key={c.label} align="center" gap={1}>
              {i > 0 && <CaretRight size={11} color={C.placeholder} />}
              {c.to ? (
                <Text
                  as="button"
                  fontSize="12.5px"
                  color={C.brand700}
                  fontWeight={500}
                  onClick={() => navigate(c.to as string)}
                  _hover={{ color: C.brand800, textDecoration: 'underline' }}
                >
                  {c.label}
                </Text>
              ) : (
                <Text fontSize="12.5px" color={C.subtle} aria-current="page">
                  {c.label}
                </Text>
              )}
            </Flex>
          ))}
        </Flex>
      )}
      <Flex align="flex-start" gap={5} wrap="wrap">
        <motion.div
          animate={{ y: [0, -3, 0] }}
          transition={{ duration: 6, repeat: Infinity, ease: 'easeInOut' }}
        >
          <IconTile icon={icon} tone={tone} size={60} iconSize={30} />
        </motion.div>
        <Box flex="1 1 320px" minW={0}>
          {eyebrow && <Eyebrow>{crumbs ? eyebrow : 'Módulo'}</Eyebrow>}
          <Heading
            as="h1"
            fontSize={{ base: '24px', md: '30px' }}
            lineHeight="36px"
            fontWeight={700}
            letterSpacing="-0.6px"
            color={C.ink}
          >
            {title}
          </Heading>
          {description && (
            <Text fontSize="14px" lineHeight="20px" color={C.muted} mt={2} maxW="680px">
              {description}
            </Text>
          )}
        </Box>
        {actions && (
          <Flex align="center" gap={2} wrap="wrap" ml="auto">
            {actions}
          </Flex>
        )}
      </Flex>
    </Box>
  );
}

// Homologação sempre visível, com o ponto âmbar pulsando (RNF03).
export function EnvBadge({ ambiente }: { ambiente?: NfseAmbiente }) {
  if (!ambiente) return null;
  const prod = ambiente === 'producao';
  return (
    <Flex
      align="center"
      gap={2}
      h="32px"
      px={3}
      borderRadius="999px"
      bg={prod ? C.successBg : C.warningSoft}
      boxShadow={`inset 0 0 0 1px ${prod ? '#BBF7D0' : C.warningLine}`}
      color={prod ? C.successText : C.warningText}
      fontFamily={MONO}
      fontSize="11px"
      fontWeight={600}
      letterSpacing="0.6px"
      textTransform="uppercase"
    >
      <motion.span
        style={{
          width: 7,
          height: 7,
          borderRadius: '50%',
          background: prod ? C.successIcon : '#F59E0B',
        }}
        animate={prod ? undefined : { opacity: [1, 0.35, 1] }}
        transition={{ duration: 1.6, repeat: Infinity }}
      />
      {prod ? 'Produção' : 'Homologação'}
    </Flex>
  );
}

// --- status ---------------------------------------------------------------------------

type TagTone = 'success' | 'warning' | 'danger' | 'info' | 'neutral';

const TAG: Record<TagTone, { bg: string; fg: string; dot: string }> = {
  success: { bg: C.successBg, fg: C.successText, dot: C.successIcon },
  warning: { bg: C.warningBg, fg: C.warningText, dot: '#D97706' },
  danger: { bg: C.dangerBg, fg: C.dangerText, dot: C.danger },
  info: { bg: C.brand100, fg: C.brand900, dot: C.brand600 },
  neutral: { bg: C.lineSoft, fg: C.ink2, dot: C.placeholder },
};

export type TagKind =
  | NfseEmissaoStatus
  | NfseEmitenteSituacao
  | NfseCertificadoSituacao
  | 'pendente';

const KIND: Record<TagKind, [TagTone, string]> = {
  rascunho: ['neutral', 'Rascunho'],
  enviando: ['info', 'Enviando'],
  autorizada: ['success', 'Autorizada'],
  rejeitada: ['danger', 'Rejeitada'],
  cancelada: ['neutral', 'Cancelada'],
  substituida: ['info', 'Substituída'],
  liberado: ['success', 'Pode emitir'],
  municipio_nao_aderente: ['warning', 'Município não aderente'],
  sem_ibge: ['warning', 'Sem código IBGE'],
  sem_certificado: ['neutral', 'Sem certificado'],
  certificado_vencido: ['danger', 'Certificado vencido'],
  procuracao_vencida: ['danger', 'Procuração vencida'],
  valido: ['success', 'Válido'],
  a_vencer: ['warning', 'A vencer'],
  vencido: ['danger', 'Vencido'],
  ausente: ['neutral', 'Não enviado'],
  pendente: ['neutral', 'Pendente'],
};

export function StatusTag({ kind, children }: { kind: TagKind; children?: React.ReactNode }) {
  const [tone, label] = KIND[kind] ?? ['neutral', kind];
  const t = TAG[tone];
  return (
    <Flex
      as="span"
      display="inline-flex"
      align="center"
      gap={1.5}
      h="24px"
      px={2.5}
      borderRadius="999px"
      bg={t.bg}
      color={t.fg}
      fontSize="12px"
      fontWeight={600}
      whiteSpace="nowrap"
      textDecoration={kind === 'cancelada' ? 'line-through' : undefined}
    >
      <Box as="span" w="6px" h="6px" borderRadius="full" bg={t.dot} flex="none" />
      {children ?? label}
    </Flex>
  );
}

// --- vidro e seções --------------------------------------------------------------------

export function Card({
  children,
  ...rest
}: React.ComponentProps<typeof GlassCard> & { children: React.ReactNode }) {
  return (
    <GlassCard intensity="strong" borderRadius="24px" {...rest}>
      {children}
    </GlassCard>
  );
}

export function CardHead({
  title,
  description,
  right,
  id,
}: {
  title: React.ReactNode;
  description?: React.ReactNode;
  right?: React.ReactNode;
  id?: string;
}) {
  return (
    <Flex align="flex-start" justify="space-between" gap={3} mb={4} wrap="wrap">
      <Box minW={0}>
        <Heading as="h2" id={id} fontSize="16px" lineHeight="24px" fontWeight={650} color={C.ink}>
          {title}
        </Heading>
        {description && (
          <Text fontSize="12.5px" color={C.subtle} mt={0.5}>
            {description}
          </Text>
        )}
      </Box>
      {right}
    </Flex>
  );
}

// Cabeçalho numerado dos cartões do formulário (1 Emitente, 2 Tomador…).
export function SectionHead({
  n,
  title,
  description,
  id,
}: {
  n: number;
  title: string;
  description?: string;
  id?: string;
}) {
  return (
    <Flex align="baseline" gap={3} mb={4} wrap="wrap">
      <Flex
        align="center"
        justify="center"
        w="24px"
        h="24px"
        borderRadius="full"
        bgImage={PILL_GRADIENT}
        color="white"
        fontFamily={MONO}
        fontSize="12px"
        fontWeight={600}
        flex="none"
        alignSelf="center"
      >
        {n}
      </Flex>
      <Heading as="h2" id={id} fontSize="16px" fontWeight={650} color={C.ink}>
        {title}
      </Heading>
      {description && (
        <Text fontSize="12.5px" color={C.subtle}>
          {description}
        </Text>
      )}
    </Flex>
  );
}

const BANNER: Record<TagTone, { bg: string; fg: string; line: string; icon: PhosphorIcon }> = {
  success: { bg: C.successSoft, fg: C.successText, line: '#BBF7D0', icon: CheckCircle },
  warning: { bg: C.warningSoft, fg: C.warningText, line: C.warningLine, icon: Prohibit },
  danger: { bg: '#FEF2F2', fg: C.dangerText, line: C.dangerLine, icon: XCircle },
  info: { bg: C.brand50, fg: C.brand900, line: '#BAE6FD', icon: Info },
  neutral: { bg: C.sunken, fg: C.ink2, line: C.line, icon: Info },
};

export function Banner({
  tone,
  icon,
  children,
  action,
  compact,
}: {
  tone: TagTone;
  icon?: PhosphorIcon;
  children: React.ReactNode;
  action?: React.ReactNode;
  compact?: boolean;
}) {
  const b = BANNER[tone];
  const Icone = icon ?? b.icon;
  return (
    <Flex
      role={tone === 'danger' ? 'alert' : undefined}
      align="flex-start"
      gap={3}
      p={compact ? '10px 12px' : '14px 16px'}
      borderRadius="18px"
      bg={b.bg}
      color={b.fg}
      boxShadow={`inset 0 0 0 1px ${b.line}`}
      fontSize="13px"
      lineHeight="19px"
    >
      <Box flex="none" pt="1px">
        <Icone size={compact ? 18 : 20} weight="duotone" />
      </Box>
      <Box flex={1} minW={0}>
        {children}
      </Box>
      {action && <Box flex="none">{action}</Box>}
    </Flex>
  );
}

// --- verificações ----------------------------------------------------------------------

export type CheckState = 'ok' | 'warn' | 'fail' | 'pending' | 'running';

export interface CheckItem {
  title: string;
  detail?: React.ReactNode;
  state: CheckState;
}

const CHECK: Record<CheckState, { bg: string; fg: string }> = {
  ok: { bg: C.successSoft, fg: C.success },
  warn: { bg: C.warningSoft, fg: C.warning },
  fail: { bg: '#FEF2F2', fg: C.danger },
  pending: { bg: C.sunken, fg: C.placeholder },
  running: { bg: C.brand50, fg: C.brand700 },
};

function CheckIcon({ state }: { state: CheckState }) {
  if (state === 'running') return <Spinner size="sm" thickness="2px" color={C.brand700} />;
  if (state === 'ok') return <CheckCircle size={19} weight="fill" />;
  if (state === 'warn') return <Warning size={19} weight="fill" />;
  if (state === 'fail') return <XCircle size={19} weight="fill" />;
  return <Circle size={19} />;
}

export function CheckList({ items }: { items: CheckItem[] }) {
  return (
    <Flex direction="column" gap={2}>
      {items.map(item => {
        const c = CHECK[item.state];
        return (
          <Flex
            key={item.title}
            align="flex-start"
            gap={3}
            p="10px 12px"
            borderRadius="14px"
            bg={c.bg}
            transition="background 0.3s"
          >
            <Flex color={c.fg} pt="1px" flex="none" w="19px" justify="center">
              <CheckIcon state={item.state} />
            </Flex>
            <Box minW={0}>
              <Text fontSize="13px" fontWeight={600} color={C.ink}>
                {item.title}
              </Text>
              {item.detail && (
                <Text fontSize="12px" color={C.subtle} mt={0.5}>
                  {item.detail}
                </Text>
              )}
            </Box>
          </Flex>
        );
      })}
    </Flex>
  );
}

// --- controles --------------------------------------------------------------------------

export function Segmented<T extends string>({
  label,
  value,
  options,
  onChange,
  isDisabled,
  id,
}: {
  label: string;
  value: T;
  options: { value: T; label: string }[];
  onChange: (v: T) => void;
  isDisabled?: boolean;
  id: string;
}) {
  return (
    <Flex
      role="radiogroup"
      aria-label={label}
      p="3px"
      gap="2px"
      borderRadius="999px"
      bg="rgba(255, 255, 255, 0.6)"
      boxShadow="inset 0 0 0 1px rgba(15, 23, 42, 0.08)"
      opacity={isDisabled ? 0.55 : 1}
      display="inline-flex"
      flexWrap="wrap"
    >
      {options.map(o => {
        const ativo = o.value === value;
        return (
          <Box
            as="button"
            type="button"
            role="radio"
            aria-checked={ativo}
            key={o.value}
            disabled={isDisabled}
            onClick={() => onChange(o.value)}
            position="relative"
            h="32px"
            px={3.5}
            borderRadius="999px"
            fontSize="13px"
            fontWeight={600}
            color={ativo ? 'white' : C.ink2}
            transition="color 0.2s"
            _focusVisible={{ boxShadow: `0 0 0 2px #FFFFFF, 0 0 0 4px ${C.accent}` }}
          >
            {ativo && (
              <motion.span
                layoutId={`seg-${id}`}
                transition={SPRING}
                style={{
                  position: 'absolute',
                  inset: 0,
                  borderRadius: 999,
                  backgroundImage: PILL_GRADIENT,
                  boxShadow: PILL_SHADOW,
                }}
              />
            )}
            <Box as="span" position="relative">
              {o.label}
            </Box>
          </Box>
        );
      })}
    </Flex>
  );
}

export function Chip({
  selected,
  count,
  onClick,
  children,
}: {
  selected: boolean;
  count?: number;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <Flex
      as="button"
      type="button"
      aria-pressed={selected}
      onClick={onClick}
      align="center"
      gap={1.5}
      h="32px"
      px={3}
      borderRadius="999px"
      fontSize="12.5px"
      fontWeight={600}
      color={selected ? 'white' : C.ink2}
      bgImage={selected ? PILL_GRADIENT : undefined}
      bg={selected ? undefined : 'rgba(255, 255, 255, 0.7)'}
      boxShadow={selected ? PILL_SHADOW : `inset 0 0 0 1px ${C.lineInput}`}
      transition="transform 0.25s cubic-bezier(0.34, 1.56, 0.64, 1)"
      _active={{ transform: 'scale(0.96)' }}
      _focusVisible={{ boxShadow: `0 0 0 2px #FFFFFF, 0 0 0 4px ${C.accent}` }}
    >
      {children}
      {count != null && (
        <Box
          as="span"
          fontFamily={MONO}
          fontSize="11px"
          px={1.5}
          borderRadius="999px"
          bg={selected ? 'rgba(255, 255, 255, 0.22)' : C.brand100}
          color={selected ? 'white' : C.brand900}
        >
          {count}
        </Box>
      )}
    </Flex>
  );
}

// --- números ------------------------------------------------------------------------------

export function CountUp({
  value,
  format = v => Math.round(v).toLocaleString('pt-BR'),
}: {
  value: number;
  format?: (v: number) => string;
}) {
  const [shown, setShown] = useState(0);
  const anterior = useRef(0);
  useEffect(() => {
    const controls = animate(anterior.current, value, {
      duration: 0.9,
      ease: [0.16, 1, 0.3, 1],
      onUpdate: v => setShown(v),
    });
    anterior.current = value;
    return () => controls.stop();
  }, [value]);
  return <>{format(shown)}</>;
}

// Legenda do sistema: vermelho < 40%, laranja até 74,99%, azul até 99,99%, verde em 100%.
export const ringColor = (pct: number) => {
  if (pct >= 100) return C.successIcon;
  if (pct >= 75) return C.brand600;
  if (pct >= 40) return '#F59E0B';
  return C.danger;
};

export function Ring({
  value,
  size = 58,
  stroke = 5,
  label,
}: {
  value: number;
  size?: number;
  stroke?: number;
  label?: string;
}) {
  const r = (size - stroke) / 2;
  const circ = 2 * Math.PI * r;
  const pct = Math.max(0, Math.min(100, value));
  return (
    <Flex align="center" gap={2}>
      <Box position="relative" w={`${size}px`} h={`${size}px`}>
        <svg width={size} height={size} role="img" aria-label={`${Math.round(pct)}%`}>
          <circle
            cx={size / 2}
            cy={size / 2}
            r={r}
            fill="none"
            stroke={C.lineSoft}
            strokeWidth={stroke}
          />
          <motion.circle
            cx={size / 2}
            cy={size / 2}
            r={r}
            fill="none"
            stroke={ringColor(pct)}
            strokeWidth={stroke}
            strokeLinecap="round"
            strokeDasharray={circ}
            initial={{ strokeDashoffset: circ }}
            animate={{ strokeDashoffset: circ * (1 - pct / 100) }}
            transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
            transform={`rotate(-90 ${size / 2} ${size / 2})`}
          />
        </svg>
        <Flex position="absolute" inset={0} align="center" justify="center">
          <Text fontFamily={MONO} fontSize={size >= 50 ? '13px' : '10px'} fontWeight={600}>
            {Math.round(pct)}%
          </Text>
        </Flex>
      </Box>
      {label && (
        <Eyebrow maxW="70px" lineHeight="13px">
          {label}
        </Eyebrow>
      )}
    </Flex>
  );
}

export function Meter({ value, color, w }: { value: number; color: string; w?: string }) {
  return (
    <Box h="6px" w={w ?? '100%'} borderRadius="999px" bg={C.lineSoft} overflow="hidden">
      <motion.div
        style={{ height: '100%', borderRadius: 999, background: color }}
        initial={{ width: 0 }}
        animate={{ width: `${Math.max(0, Math.min(100, value))}%` }}
        transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
      />
    </Box>
  );
}

// --- gaveta ---------------------------------------------------------------------------------

export function NfseDrawer({
  isOpen,
  onClose,
  icon,
  tone = 'blue',
  title,
  subtitle,
  right,
  footer,
  size = 'lg',
  children,
}: {
  isOpen: boolean;
  onClose: () => void;
  icon: PhosphorIcon;
  tone?: Tone;
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  right?: React.ReactNode;
  footer?: React.ReactNode;
  size?: 'md' | 'lg' | 'xl';
  children: React.ReactNode;
}) {
  return (
    <Drawer isOpen={isOpen} onClose={onClose} placement="right" size={size}>
      <DrawerOverlay bg="rgba(15, 23, 42, 0.22)" backdropFilter="blur(6px)" />
      <DrawerContent
        m={{ base: 0, md: 3 }}
        borderRadius={{ base: 0, md: '24px' }}
        bg="rgba(255, 255, 255, 0.94)"
        boxShadow="inset 0 1px 0 rgba(255, 255, 255, 0.9), 0 30px 80px -20px rgba(15, 23, 42, 0.35)"
        overflow="hidden"
      >
        <DrawerHeader
          display="flex"
          alignItems="center"
          gap={3}
          borderBottomWidth="1px"
          borderColor={C.line}
        >
          <IconTile icon={icon} tone={tone} size={36} iconSize={17} />
          <Box flex={1} minW={0}>
            <Text fontSize="15px" fontWeight={700} color={C.ink} noOfLines={1}>
              {title}
            </Text>
            {subtitle && (
              <Text fontSize="12.5px" fontWeight={400} color={C.subtle} noOfLines={1}>
                {subtitle}
              </Text>
            )}
          </Box>
          {right}
          <NIconButton icon={X} label="Fechar" onClick={onClose} />
        </DrawerHeader>
        <DrawerBody py={5}>{children}</DrawerBody>
        {footer && (
          <DrawerFooter gap={2} borderTopWidth="1px" borderColor={C.line}>
            {footer}
          </DrawerFooter>
        )}
      </DrawerContent>
    </Drawer>
  );
}

// --- formatos -------------------------------------------------------------------------------

export const brl = (v?: number | null) =>
  (Number(v) || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

export const dataBR = (iso?: string | null) =>
  iso ? new Date(iso).toLocaleDateString('pt-BR', { timeZone: 'America/Sao_Paulo' }) : '—';

export const horaBR = (iso?: string | null) =>
  iso
    ? new Date(iso).toLocaleTimeString('pt-BR', {
        hour: '2-digit',
        minute: '2-digit',
        timeZone: 'America/Sao_Paulo',
      })
    : '';

export const cnpjMask = (s?: string | null) => {
  const d = String(s ?? '').replace(/\D/g, '');
  if (d.length === 11) return d.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
  if (d.length !== 14) return s ?? '';
  return d.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
};

// Chave de acesso em blocos de 4, como no DANFSe.
export const agruparChave = (k?: string | null) => (k ?? '').replace(/(\d{4})(?=\d)/g, '$1 ');
